import React from "react";
import { Link } from "react-router-dom";
import {
  Target,
  Eye,
  Heart,
  Lightbulb,
  Users,
  Globe,
  TrendingUp,
  CheckCircle,
  Calendar,
  ArrowRight,
  Mail,
  Linkedin,
} from "lucide-react";

const About: React.FC = () => {
  const values = [
    {
      icon: Lightbulb,
      title: "Innovation First",
      description: "Pushing AI boundaries for real hiring challenges."
    },
    {
      icon: Heart,
      title: "Human-Centric",
      description: "Technology should enhance, not replace human decisions."
    },
    {
      icon: CheckCircle,
      title: "Quality & Accuracy",
      description: "Ensuring fairness and high standards in every assessment."
    },
    {
      icon: Users,
      title: "Inclusive Growth",
      description: "Reducing bias and creating equal opportunities."
    }
  ];

  const timeline = [
    {
      year: "2025",
      title: "Founded with a mission",
      description: "Founded with a mission to close the hiring gap."
    },
    {
      year: "2025",
      title: "MVP Development",
      description: "MVP development with AI-driven skill verification and recruiter dashboards."
    },
    {
      year: "Sep 2025",
      title: "Pilot Launch",
      description: "Pilot launch with early adopters (HRs & candidates)."
    },
    {
      year: "2026 (Goal)",
      title: "Global Expansion",
      description: "Expand to enterprise customers and scale globally."
    }
  ];

  const team = [
    {
      name: "Sathya Naga Sai Vakacharla",
      role: "Founder & CEO",
      description: "Leading product, strategy, and pilot launch.",
      image: "https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg?auto=compress&cs=tinysrgb&w=300"
    },
    {
      name: "Manoj",
      role: "Full Stack Developer",
      description: "Engineering the VetriaAI platform.",
      image: "https://images.pexels.com/photos/3777931/pexels-photo-3777931.jpeg?auto=compress&cs=tinysrgb&w=300"
    },
    {
      name: "Sanjita Kumari",
      role: "Full Stack Developer",
      description: "Building scalable hiring solutions.",
      image: "https://images.pexels.com/photos/3756681/pexels-photo-3756681.jpeg?auto=compress&cs=tinysrgb&w=300"
    },
    {
      name: "Ruchi Tiwary",
      role: "Full Stack Developer",
      description: "Building scalable hiring solutions.",
      image: "/Ruchi_Tiwary.jpeg"
    },
    {
      name: "Janani",
      role: "Operations Head",
      description: "Driving execution and process efficiency.",
      image: "https://images.pexels.com/photos/3756706/pexels-photo-3756706.jpeg?auto=compress&cs=tinysrgb&w=300"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Decorative Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <nav className="glass-card shadow-lg px-6 py-4 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="/vetriaAI.png"
              alt="VetriaAI Logo"
              className="w-8 h-8"
            />
            <span className="text-xl font-bold text-gray-900">VetriaAI</span>
          </Link>
          <div className="flex items-center space-x-6">
            <Link
              to="/"
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-gradient font-semibold"
            >
              About
            </Link>
            <Link
              to="/features"
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Features
            </Link>
            <Link
              to="/contact"
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Contact
            </Link>
            <Link
              to="/login"
              className="btn-primary"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 px-6">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">About VetriaAI</h1>
          <p className="text-xl text-gray-600 mb-8">
            Transforming the future of hiring with artificial intelligence
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="glass-card hover:-translate-y-2">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center mr-4">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                To democratize access to top talent by making the hiring process more efficient,
                unbiased, and data-driven through artificial intelligence.
              </p>
            </div>
            <div className="glass-card hover:-translate-y-2">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center mr-4">
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Our Vision</h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                A world where hiring decisions are based on skills, merit, and potential—free
                from bias or superficial judgments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Core Values</h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="glass-card hover:-translate-y-2 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="py-20 px-6 relative">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600">
              From inception to global impact
            </p>
          </div>
          <div className="space-y-6">
            {timeline.map((item, index) => (
              <div key={index} className="flex items-start gap-6">
                <div className="flex-shrink-0 w-24">
                  <div className="px-4 py-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 text-white text-sm font-semibold text-center">
                    {item.year}
                  </div>
                </div>
                <div className="glass-card flex-1 hover:-translate-y-2">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Our Team */}
      <section className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600">
              The passionate individuals building the future of hiring
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="glass-card hover:-translate-y-2 text-center">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover ring-4 ring-white shadow-lg"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
                <p className="text-gradient font-medium mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="glass-card-gradient">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Join Our Mission</h2>
            <p className="text-xl text-gray-600 mb-8">
              Ready to be part of the hiring revolution? Let's transform recruitment together.
            </p>
            <Link
              to="/contact"
              className="btn-primary"
            >
              Get in Touch <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="glass-card mt-12 text-gray-900 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="/vetriaAI.png"
                  alt="VetriaAI Logo"
                  className="w-8 h-8"
                />
                <span className="text-xl font-bold">VetriaAI</span>
              </div>
              <p className="text-gray-600">
                Revolutionizing recruitment with AI-powered intelligence.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-gradient">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/features" className="text-gray-600 hover:text-gray-900">Features</Link>
                </li>
                <li>
                  <Link to="/security" className="text-gray-600 hover:text-gray-900">Security</Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-gradient">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-gray-600 hover:text-gray-900">About</Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-600 hover:text-gray-900">Contact</Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-gradient">Contact</h4>
              <p className="text-gray-600">info@vetriaai.com</p>
              <p className="text-gray-600">India</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-gradient">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/cancellations-refunds" className="text-gray-600 hover:text-gray-900">
                    Cancellations & Refunds
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-600 hover:text-gray-900">
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-gray-600 hover:text-gray-900">
                    Shipping (Digital)
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-600 hover:text-gray-900">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-8 mt-8 text-center text-gray-600">
            <p>2025 VetriaAI. All rights and trademarks are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
